// Pairing a fresh PC to a venue and station (spec/10 §3.2, spec/13 §12; F-STN-02, #80). The backend
// for the venue console's "add a PC" flow.
//
//   POST /v1/device-pairings                                   public, 10/hour/IP: the PC starts,
//                                                              gets an 8-character code (30 min)
//   POST /v1/organizations/{o}/device-pairings/{code}/approve  stations:manage: a staff member picks
//                                                              the venue and the station
//   GET  /v1/device-pairings/{pairingId}                       public: the PC polls until approved
//
// The PC has no session — it *is* being enrolled — so start and poll are public and identify the
// pairing by an opaque id it is given, never by the org. **Approve is organisation-scoped**: the
// pairing is unowned until a staff member in an organisation approves it into their venue, so it
// reuses the standard `stations:manage` guard and the venue check the console already carries.
//
// The station is `stations`' resource (F-ORG-03, #497): approve creates one through the same service
// the console does, or re-pairs an existing one, and binds the device (F-STN-05) to it.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { DevicePairingIdSchema, StationIdSchema, VenueIdSchema } from "../ids/schema.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { STATIONS_MANAGE } from "../permissions/index.js";
import { CreateStationSchema, StationRoleSchema, StationSchema } from "./stations.js";
// ---------------------------------------------------------------------------------------------
// POST /v1/device-pairings — the PC starts
// ---------------------------------------------------------------------------------------------
/** The Windows version a fresh PC reports (spec/10 §2). Named so the generated C# has a type for it. */
export const HardwareOsSchema = z
    .object({ build: z.string().min(1).max(64), edition: z.string().min(1).max(64) })
    .openapi('HardwareOs', { description: 'The Windows build and edition (e.g. `26100.4061`, `Pro`).' });
/** The hardware a fresh PC reports, for the console and for Wake-on-LAN later (spec/10 §3.2, §6.5). */
export const HardwareSummarySchema = z
    .object({
    hostname: z.string().min(1).max(64),
    os: HardwareOsSchema,
    /** The NIC MAC addresses, for Wake-on-LAN (spec/10 §6.5). Upper-case, colon-separated. */
    macAddresses: z.array(z.string().regex(/^([0-9A-F]{2}:){5}[0-9A-F]{2}$/)).max(8),
})
    .openapi('HardwareSummary', { description: "A fresh PC's hardware summary, shown to staff and used for Wake-on-LAN." });
/**
 * A boot attestation the Station app passes with its pairing request (spec/10 §14.2, ADR-0065 decision 4): the compact JWS
 * its Clavian Boot client got from the venue's boot server, and the nonce the Station app made for this attempt (the
 * attestation must carry the same one). Never trusted on its own: the cloud verifies it, and any failure is today's code
 * pairing.
 */
export const DevicePairingBootAttestationSchema = z
    .object({
    /** The compact JWS `<protected>.<payload>.<signature>` (a `BootPairingAttestation`, signed with the boot server's device key). */
    attestation: z.string().min(16).max(4096),
    /** 16 random bytes, base64url without padding (22 characters), made by the Station app for this pairing attempt. */
    nonce: z.string().regex(/^[A-Za-z0-9_-]{22}$/),
})
    .openapi('DevicePairingBootAttestation', {
    description: 'A signed boot attestation from the venue’s boot server, and the nonce it was made for (spec/10 §14.2).',
});
/**
 * Why the cloud did not take a boot attestation (spec/10 §14.2). Said back to the station for its log only; the station
 * then pairs by code, as today. Never shown to staff.
 */
export const DEVICE_PAIRING_BOOT_REFUSALS = [
    'bad_signature',
    'unknown_boot_server',
    'wrong_audience',
    'expired',
    'venue_mismatch',
    'not_a_boot_pc',
    'not_linked',
    'not_station_client',
    'station_has_device',
    'boot_not_live',
    'nonce_mismatch',
    'key_mismatch',
    'replayed',
];
export const DevicePairingBootRefusalSchema = z.enum(DEVICE_PAIRING_BOOT_REFUSALS).openapi('DevicePairingBootRefusal', {
    description: 'Why a boot attestation was not taken: the station pairs by code instead (spec/10 §14.2). For the station’s log.',
});
export const DevicePairingStartRequestSchema = z
    .object({
    /** The device's Ed25519 public key, base64url of the raw 32 bytes (F-STN-05). Bound to the station on approval. */
    publicKey: z.string().min(1).max(128),
    hardware: HardwareSummarySchema,
    /**
     * A **stable hardware fingerprint** derived from the summary (spec/10 §3.2, ADR-0022): a subset
     * that survives a reimage — today the sorted set of physical MAC addresses, hashed. The api uses
     * it to **auto re-link a reimaged PC** to the station it was last bound to at its venue, without a
     * pairing code (gated by `stations.autoRelinkKnownHardware`, audited). Absent when the PC has no
     * stable hardware to fingerprint, and then it always pairs by code.
     */
    hardwareFingerprint: z.string().min(1).max(128).optional(),
    /**
     * What the device will be (F-STN-17, 2026-10-01): `counter` for a PC installed with the counter role (Clavian
     * Counter), which the approval binds as a counter — never bookable, never on the floor. Absent is `station`.
     */
    role: StationRoleSchema.optional(),
    /**
     * A boot attestation from the venue's Clavian boot server (spec/10 §14.2, 2026-10-17): sent by a PC that starts from
     * it. When it verifies and the PC's boot PC is linked to a station waiting for its app, the PC is paired to that
     * station with no code (`boot_attested`). Absent, or refused, the pairing is exactly as without it.
     */
    bootAttestation: DevicePairingBootAttestationSchema.optional(),
})
    .openapi('DevicePairingStartRequest', { description: 'A fresh PC starting to pair: its key, its hardware and a stable fingerprint.' });
/** What starting a pairing did (spec/10 §3.2, §14.2; ADR-0022, ADR-0065). */
export const DEVICE_PAIRING_START_OUTCOMES = ['code', 'relinked', 'boot_attested'];
export const DevicePairingStartOutcomeSchema = z.enum(DEVICE_PAIRING_START_OUTCOMES).openapi('DevicePairingStartOutcome', {
    description: 'What starting a pairing did: `code` — show the 8-character code and poll for a staff approval; ' +
        '`relinked` — the api recognised this PC’s hardware and re-bound it to its station automatically (no code); ' +
        '`boot_attested` — the venue’s boot server vouched for this PC and the api paired it to the station its boot PC is ' +
        'linked to (no code).',
});
export const DevicePairingStartedSchema = z
    .object({
    outcome: DevicePairingStartOutcomeSchema,
    /** The pairing to poll, always present — a row is written for a code and for an auto re-link alike. */
    id: DevicePairingIdSchema,
    /**
     * The 8-character code to show full-screen with a QR (spec/10 §3.2 rule 2). On a `relinked`
     * outcome it is the (already-approved) pairing's own code — there is nothing to show, so the PC
     * ignores it and uses `station`; the field stays present for a uniform, non-breaking shape.
     */
    code: z.string().length(8),
    expiresAt: IsoDateTimeSchema,
    /**
     * The station the PC was auto re-linked to (`relinked`), or paired to on its boot server's word
     * (`boot_attested`): it is already bound — the PC skips the code and goes straight to device-auth
     * (spec/10 §3.2, §3.3, §14.2). `null` when a code must be shown.
     */
    station: StationSchema.nullable(),
    /**
     * Why a boot attestation the request carried was not taken (spec/10 §14.2), for the station's log only; absent when
     * the request carried none or it was taken. Optional, an addition.
     */
    bootRefusal: DevicePairingBootRefusalSchema.optional(),
})
    .openapi('DevicePairingStarted', {
    description: 'The pairing id to poll and, for a code outcome, the 8-character code (valid 30 minutes); for a ' +
        'relinked outcome the station the PC was auto re-linked to and a `null` code (spec/10 §3.2, ADR-0022).',
});
export const createDevicePairingRoute = createRoute({
    method: 'post',
    path: '/v1/device-pairings',
    operationId: 'createDevicePairing',
    tags: ['stations'],
    summary: 'A fresh PC starts pairing: get a code to show',
    description: 'Public and rate-limited to 10 per hour per IP (spec/10 §3.2). The PC sends its device public key and a ' +
        'hardware summary, and gets back an 8-character code (valid 30 minutes) to show full-screen with a QR, and a ' +
        'pairing id to poll. A staff member approves it with `POST …/device-pairings/{code}/approve`.',
    request: { body: { content: { 'application/json': { schema: DevicePairingStartRequestSchema } }, required: true } },
    responses: {
        201: jsonResponse(DevicePairingStartedSchema, 'The pairing code and id.'),
        400: commonProblemResponses[400],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// POST /v1/organizations/{o}/device-pairings/{code}/approve — a staff member approves
// ---------------------------------------------------------------------------------------------
/** How to bind the PC (spec/10 §3.2 rule 3). The fields each mode needs are validated by the server. */
export const STATION_PAIRING_MODES = ['new', 'existing'];
export const StationPairingModeSchema = z.enum(STATION_PAIRING_MODES).openapi('StationPairingMode', {
    description: 'A brand-new station (`new`, defined here) or an existing one being re-paired (`existing`).',
});
/**
 * Bind the PC to a new station, defined through the same shape the console uses (`CreateStation`,
 * F-ORG-03), or to one that already exists — a hardware replacement (spec/10 §3.2 rule 3). The server
 * checks the fields each `mode` needs: `station` for `new`, `stationId` for `existing`.
 */
export const ApproveDevicePairingRequestSchema = z
    .object({
    venueId: VenueIdSchema,
    mode: StationPairingModeSchema,
    /** `new`: the station to define (its number, class, kind, …), as the console defines one. */
    station: CreateStationSchema.optional(),
    /** `existing`: the station to re-pair. */
    stationId: StationIdSchema.optional(),
})
    .openapi('ApproveDevicePairingRequest', {
    description: 'Which venue and station this PC becomes: a new station, or an existing one.',
});
export const ApproveDevicePairingParamsSchema = OrganizationScopeParamsSchema.extend({
    code: z.string().length(8).openapi({ param: { name: 'code', in: 'path' }, description: 'The pairing code the PC shows.' }),
});
export const approveDevicePairingRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/device-pairings/{code}/approve',
    operationId: 'approveDevicePairing',
    tags: ['stations'],
    summary: 'Approve a pairing: bind the PC to a venue and station',
    description: 'Needs `stations:manage` at the chosen venue (spec/10 §3.2 rule 3). Defines a new station (or re-pairs an ' +
        'existing one) and binds the PC — its device key becomes a bound `device` (F-STN-05) on the station — then ' +
        "moves the pairing to `approved` so the PC's next poll learns what it is. Audited with the actor, venue and station.",
    [PERMISSION_EXTENSION]: STATIONS_MANAGE,
    request: {
        params: ApproveDevicePairingParamsSchema,
        body: { content: { 'application/json': { schema: ApproveDevicePairingRequestSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(StationSchema, 'The station the PC is now bound to.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such pairing, venue or station in this organisation, or the code has expired.', 'not-found'),
        409: problemResponse('A station at this venue already has that number, or the pairing was already approved.', 'conflict'),
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// GET /v1/device-pairings/{pairingId} — the PC polls
// ---------------------------------------------------------------------------------------------
export const DEVICE_PAIRING_STATUSES = ['pending', 'approved', 'expired'];
export const DevicePairingStatusSchema = z.enum(DEVICE_PAIRING_STATUSES).openapi('DevicePairingStatus', {
    description: 'Where the pairing is: `pending` (waiting for staff), `approved`, or `expired`.',
});
export const DevicePairingSchema = z
    .object({
    id: DevicePairingIdSchema,
    status: DevicePairingStatusSchema,
    /** The station the PC is bound to, once `approved` (spec/10 §3.2 rule 4). `null` while pending or expired. */
    station: StationSchema.nullable(),
})
    .openapi('DevicePairing', { description: 'A pairing the PC polls until a staff member approves it (spec/10 §3.2).' });
export const GetDevicePairingParamsSchema = z.object({
    pairingId: DevicePairingIdSchema.openapi({ param: { name: 'pairingId', in: 'path' } }),
});
export const getDevicePairingRoute = createRoute({
    method: 'get',
    path: '/v1/device-pairings/{pairingId}',
    operationId: 'getDevicePairing',
    tags: ['stations'],
    summary: 'Poll a pairing until it is approved',
    description: 'Public (spec/10 §3.2 rule 4): the PC polls with the id it was given until `status` is `approved`, then reads ' +
        'the station it is bound to. An unknown id is `404`.',
    request: { params: GetDevicePairingParamsSchema },
    responses: {
        200: jsonResponse(DevicePairingSchema, 'The pairing and, once approved, the station.'),
        404: problemResponse('No such pairing.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
/**
 * The audit action of a code-free pairing on a boot server's word (spec/10 §14.2): it names the station, the boot PC, the
 * boot server, the MAC and the boot id — never the attestation's bytes.
 */
export const STATION_PAIRED_BY_BOOT_AUDIT_ACTION = 'stations.station.paired_by_boot';
export const DEVICE_PAIRING_ROUTES = [createDevicePairingRoute, approveDevicePairingRoute, getDevicePairingRoute];
//# sourceMappingURL=device-pairings.js.map